import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Hero from '@/components/Hero';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { homeCopy } from '@/contexts/language/translations/pages/home';

// How long each slide stays up before the carousel advances on its own (driven by the dot fill animation).
const SLIDE_DURATION_MS = 7000;

// Accent words rise into place one after another (see .hero-build-word in index.css).
// Hero is keyed by slide, so the animation replays every time a slide comes in.
// `slow` is for short accents (slide 1's "Build On"): two words go by too fast at the default pace.
const BuildUp = ({ text, slow = false }: { text: string; slow?: boolean }) =>
  text.split(' ').flatMap((word, i) => [
    i > 0 ? ' ' : null,
    <span
      key={i}
      className="hero-build-word"
      style={{
        animationDuration: slow ? '1.3s' : '0.6s',
        animationDelay: slow ? `${0.5 + i * 0.55}s` : `${0.3 + i * 0.15}s`,
      }}
    >
      {word}
    </span>,
  ]);

const HeroSection = () => {
  const { t, language } = useLanguage();
  const c = homeCopy[language].hero;
  const [paused, setPaused] = useState(false);

  // Slide 1 kicker/subtitle/CTAs come from translations/home.ts (t keys); the rest of the
  // slide copy lives in translations/pages/home.ts (en + sk).
  const slides = [
    {
      kicker: t('heroKicker'),
      title: (
        <>
          <span className="text-foreground">{c.slide1.line1}</span>
          <br />
          <span className="text-accent"><BuildUp text={c.slide1.accent} slow /></span>
        </>
      ),
      subtitle: t('heroSubtitle'),
      ctaText: t('heroExploreCtaText'),
      ctaLink: '/how-it-works',
      secondaryCtaText: t('heroCtaText'),
      secondaryCtaNote: c.ctaNote,
      secondaryCtaLink: '/book-a-call',
    },
    {
      kicker: c.slide2.kicker,
      title: (
        <>
          <span className="text-foreground">{c.slide2.line1}</span>
          <br />
          <span className="text-accent"><BuildUp text={c.slide2.accent} /></span>
        </>
      ),
      subtitle: c.slide2.subtitle,
      ctaText: c.slide2.cta,
      ctaLink: '/solutions/retail-automation-suite',
      secondaryCtaText: t('heroCtaText'),
      secondaryCtaNote: c.ctaNote,
      secondaryCtaLink: '/book-a-call',
    },
    {
      kicker: c.slide3.kicker,
      title: (
        <>
          <span className="text-foreground">{c.slide3.before}</span>
          <span className="text-accent"><BuildUp text={c.slide3.accent} /></span>
          <br />
          <span className="text-foreground">{c.slide3.line2}</span>
        </>
      ),
      subtitle: c.slide3.subtitle,
      ctaText: c.slide3.cta,
      ctaLink: '/our-work/digital-cfo',
      secondaryCtaText: t('heroCtaText'),
      secondaryCtaNote: c.ctaNote,
      secondaryCtaLink: '/book-a-call',
    },
    {
      kicker: c.slide4.kicker,
      title: (
        <>
          <span className="text-foreground">{c.slide4.line1}</span>
          <br />
          <span className="text-accent"><BuildUp text={c.slide4.accent} /></span>
        </>
      ),
      subtitle: c.slide4.subtitle,
      ctaText: c.slide4.cta,
      ctaLink: '/our-work/data-engineering',
      secondaryCtaText: t('heroCtaText'),
      secondaryCtaNote: c.ctaNote,
      secondaryCtaLink: '/book-a-call',
    },
    {
      kicker: c.slide5.kicker,
      title: (
        <>
          <span className="text-foreground">{c.slide5.line1}</span>
          <br />
          <span className="text-accent"><BuildUp text={c.slide5.accent} /></span>
        </>
      ),
      subtitle: c.slide5.subtitle,
      ctaText: c.slide5.cta,
      ctaLink: '/our-work/context-engineering',
      secondaryCtaText: t('heroCtaText'),
      secondaryCtaNote: c.ctaNote,
      secondaryCtaLink: '/book-a-call',
    },
  ];

  const [activeSlide, setActiveSlide] = useState(0);
  const goTo = (delta: number) => setActiveSlide((prev) => (prev + delta + slides.length) % slides.length);
  const slide = slides[activeSlide];

  return (
    // Full bleed: pull the carousel out over the homepage side padding (px-7 / md:px-10 on main in Index.tsx)
    <div
      className="relative -mx-7 md:-mx-10"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <Hero
        key={activeSlide}
        kicker={slide.kicker}
        title={slide.title}
        subtitle={slide.subtitle}
        ctaText={slide.ctaText}
        ctaLink={slide.ctaLink}
        secondaryCtaText={slide.secondaryCtaText}
        secondaryCtaLink={slide.secondaryCtaLink}
        secondaryCtaNote={slide.secondaryCtaNote}
        showSwitchingText={false}
        theme="dark"
        className="pt-32 pb-6 md:pt-40 md:pb-8"
      />

      {/* Arrows only from xl up — below that, the longest slide titles (nowrap) reach far enough right/left to collide with them at lg. Dots below cover mobile/tablet/lg navigation. */}
      <button
        type="button"
        onClick={() => goTo(-1)}
        aria-label={c.prevSlide}
        className="hidden xl:flex absolute left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-accent text-accent-foreground items-center justify-center shadow-lg hover:bg-accent/90 transition-colors"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        type="button"
        onClick={() => goTo(1)}
        aria-label={c.nextSlide}
        className="hidden xl:flex absolute right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-accent text-accent-foreground items-center justify-center shadow-lg hover:bg-accent/90 transition-colors"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setActiveSlide(i)}
            aria-label={`${c.goToSlide} ${i + 1}`}
            aria-current={i === activeSlide}
            className={`relative h-2 rounded-full overflow-hidden transition-all ${i === activeSlide ? 'w-6 bg-white/30' : 'w-2 bg-white/30 hover:bg-white/50'}`}
          >
            {/* Fill = time left on this slide; pauses on hover, and finishing it moves to the next slide.
                With reduced motion the fill is static (see index.css), so the carousel never auto-advances. */}
            {i === activeSlide && (
              <span
                key={activeSlide}
                className="hero-dot-fill absolute inset-0 bg-accent rounded-full"
                style={{ animationDuration: `${SLIDE_DURATION_MS}ms`, animationPlayState: paused ? 'paused' : 'running' }}
                onAnimationEnd={() => goTo(1)}
              />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default HeroSection;
